import { useEffect, useMemo, useState } from 'react';
import { endpoints, type PiChainNode, type PiChainResult, type PiColony, type PiFlowNode, type PiProduct } from '../api';
import { EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, Stat, Stats, dateTime, number } from '../components';
import { useApiQuery } from '../hooks';
import { TypeImage } from './workflow';
import './workflow.css';
import './planetary.css';

/** "14 h 20 m" / "3 d 4 h": the two leading units are all a PI timer needs. */
function span(seconds: number) {
  const minutes = Math.max(0, Math.floor(seconds / 60));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days) return hours ? `${days} d ${hours} h` : `${days} d`;
  if (hours) return minutes % 60 ? `${hours} h ${minutes % 60} m` : `${hours} h`;
  return `${minutes} m`;
}

function until(iso: string | null) { return iso ? (new Date(iso).getTime() - Date.now()) / 1000 : 0; }

function colonyState(colony: PiColony) {
  const left = until(colony.extractorEndsAt);
  if (colony.status === 'stopped') return colony.extractorEndsAt && left < 0 ? `■ PARADO HACE ${span(-left).toUpperCase()}` : '■ PARADO';
  if (colony.status === 'soon') return `▲ PARA EN ${span(left).toUpperCase()}`;
  if (colony.status === 'running') return `● ${span(left).toUpperCase()}`;
  return colony.status === 'factory' ? '◇ FÁBRICA' : '? SIN DETALLE';
}

function extractorState(node: Extract<PiFlowNode, { kind: 'extractor' }>) {
  const left = until(node.endsAt);
  if (node.status === 'stopped') return node.endsAt ? `■ parado hace ${span(-left)}` : '■ sin programa';
  return `${node.status === 'soon' ? '▲' : '●'} para en ${span(left)}`;
}

const FACTORY_LABELS = ['', 'Basic', 'Advanced', 'Advanced', 'High-Tech'];

function FlowNode({ node }: { node: PiFlowNode }) {
  if (node.kind === 'extractor') return <div className={`pi-node pi-node--${node.status}`}>
    {node.typeId ? <TypeImage typeId={node.typeId} variant="icon" size={32} /> : <span className="wf-image pi-blank" />}
    <div className="pi-node-body">
      <strong>{node.name} <small>P0</small></strong>
      <small className="mono">ECU × {number(node.count)} · {number(node.heads)} cabezales{node.perHour > 0 ? ` · ${number(Math.round(node.perHour))}/h` : ''}</small>
      <b className="pi-node-state mono">{extractorState(node)}</b>
    </div>
  </div>;
  if (node.kind === 'factory') return <div className="pi-node">
    <TypeImage typeId={node.typeId} variant="icon" size={32} />
    <div className="pi-node-body">
      <strong>{node.name} <small>P{node.tier}</small></strong>
      <small className="mono">{FACTORY_LABELS[node.tier]} × {number(node.count)} · {number(node.outputPerCycle)} / {span(node.cycleSeconds)}</small>
    </div>
  </div>;
  const role = node.role === 'import' ? ' · importa' : node.role === 'export' ? ' · exporta' : '';
  const full = node.fillPercent !== null && node.fillPercent >= 80;
  return <div className="pi-node">
    <TypeImage typeId={node.typeId} variant="icon" size={32} />
    <div className="pi-node-body">
      <strong>{node.name}{role}</strong>
      <small className="mono">{node.contents.length ? node.contents.map((row) => `${row.name} ${number(row.amount)}`).join(' · ') : 'Vacío'}</small>
      {node.fillPercent !== null && <span className={`pi-fill${full ? ' pi-fill--full' : ''}`} role="img" aria-label={`${node.fillPercent} % lleno`}>
        <i style={{ width: `${Math.min(100, node.fillPercent)}%` }} /><small className="mono">{full ? '▲ ' : ''}{node.fillPercent} %</small>
      </span>}
    </div>
  </div>;
}

function ColoniesView() {
  const query = useApiQuery(endpoints.colonies);
  const colonies = query.data?.colonies ?? [];
  const oldest = colonies.reduce<string | null>((value, colony) => (colony.lastUpdate && (!value || colony.lastUpdate < value) ? colony.lastUpdate : value), null);
  const stopped = colonies.reduce((sum, colony) => sum + colony.stoppedExtractors, 0);
  const soon = colonies.reduce((sum, colony) => sum + colony.soonExtractors, 0);

  return <>
    {query.loading && !query.data && <LoadingState />}
    {query.error && <ErrorState error={query.error} onRetry={() => void query.reload()} />}
    {query.data && colonies.length === 0 && <EmptyState title="Sin colonias" detail=" Aparecerán al sincronizar un personaje con acceso PI." />}
    {colonies.length > 0 && <>
      <Stats>
        <Stat label="Colonias" value={number(colonies.length)} />
        <Stat label="Extractores parados" value={stopped ? `■ ${number(stopped)}` : '0'} tone={stopped ? 'neg' : undefined} />
        <Stat label="Paran en menos de 24 h" value={soon ? `▲ ${number(soon)}` : '0'} />
        <Stat label="Vista en juego más antigua" value={oldest ? span(-until(oldest)) : '—'} detail={dateTime(oldest)} />
      </Stats>
      <div className="pi-colonies">{colonies.map((colony) => <article className={`pi-colony pi-colony--${colony.status}`} key={colony.colonyId}>
        <header className="pi-colony-head">
          <strong>{colony.planetName}</strong>
          <small>{colony.planetType}{colony.upgradeLevel !== null && colony.upgradeLevel !== undefined ? ` · CC ${colony.upgradeLevel}` : ''}</small>
          <span className={`pi-pill pi-pill--${colony.status}`}>{colonyState(colony)}</span>
          <small className="mono pi-seen">{colony.lastUpdate ? `vista en juego hace ${span(-until(colony.lastUpdate))}` : 'sin vista en juego'}</small>
        </header>
        {colony.stages.length === 0
          ? <p className="muted pi-flow">Sin detalle de pines: sincroniza este piloto.</p>
          : <div className="pi-flow" role="region" aria-label={`Flujo de ${colony.planetName}`} tabIndex={0}>{colony.stages.map((stage, index) => <div className="pi-stage-wrap" key={index}>
            {index > 0 && <span className="pi-arrow" aria-hidden="true" />}
            <div className="pi-stage">{stage.map((node, position) => <FlowNode key={position} node={node} />)}</div>
          </div>)}</div>}
      </article>)}</div>
      <Section title="Producción nominal">
        <div className="table-wrap"><table className="pi-table"><thead><tr><th>Producto</th><th>Tier</th><th>Colonias</th><th className="num">Por día</th></tr></thead><tbody>{query.data?.production.map((row) => <tr key={row.typeId}>
          <td><span className="wf-cell"><TypeImage typeId={row.typeId} variant="icon" size={24} />{row.name}</span></td>
          <td className="muted">P{row.tier}</td>
          <td className="muted">{row.colonies.map((colony) => `${colony.name}${colony.stopped ? ' · ■ parado' : ''}`).join(', ')}</td>
          <td className="num">{number(Math.round(row.perDay))}</td>
        </tr>)}</tbody></table></div>
        <p className="fine">Ritmo programado de extractores y fábricas: no detecta una fábrica sin input salvo que su P0 esté parado en el mismo planeta. El contenido del launchpad es el de la última vista en juego: ESI solo lo refresca al abrir la colonia en el cliente. La hora de parada del extractor sí es fiable.</p>
      </Section>
    </>}
  </>;
}

function ChainNode({ node, root = false }: { node: PiChainNode; root?: boolean }) {
  const tone = node.missing === 0 ? 'covered' : node.colonies.length ? 'build' : 'missing';
  const amounts = root
    ? `${number(node.required)} uds. · ${number(node.cycles)} ciclos`
    : node.tier === 0
      ? <>{number(node.required)} req.{node.covered > 0 && ` · ${number(node.covered)} cub.`} · {node.missing > 0 ? <b className="wf-missing">{number(node.missing)} a extraer</b> : '✓ completo'}</>
      : <>{number(node.required)} req. · {number(node.covered)} cub. · {node.missing > 0 ? <><b className="wf-missing">{number(node.missing)} faltan</b> · {number(node.cycles)} ciclos</> : '✓ completo'}</>;
  return <div className="wf-branch">
    <article className={`wf-node wf-node--${tone}`}>
      <div className="wf-node-head">
        <TypeImage typeId={node.typeId} variant="icon" size={32} />
        <div className="wf-node-title">
          <strong>{node.name} <small>P{node.tier}</small></strong>
          <small className="mono">{amounts}</small>
          {node.missing > 0 && (node.colonies.length
            ? <small className="mono pi-source">◆ {node.colonies.join(', ')}</small>
            : <small className="mono wf-missing">■ ninguna colonia</small>)}
        </div>
      </div>
    </article>
    {node.children.length > 0 && <ul className="wf-children">{node.children.map((child, index) => <li key={`${child.typeId}-${index}`}><ChainNode node={child} /></li>)}</ul>}
  </div>;
}

function PlanetMatrix({ result }: { result: PiChainResult }) {
  const counts = result.planetTypes.map((planet) => result.extraction.filter((row) => row.planetTypes.includes(planet)).length);
  const best = Math.max(...counts);
  return <div className="table-wrap"><table className="pi-table pi-matrix"><thead><tr><th>P0</th><th className="num">A extraer</th>{result.planetTypes.map((planet) => <th key={planet}>{planet}</th>)}</tr></thead>
    <tbody>{result.extraction.map((row) => <tr key={row.typeId}>
      <td><span className="wf-cell"><TypeImage typeId={row.typeId} variant="icon" size={24} />{row.name}</span></td>
      <td className="num">{number(row.quantity)}</td>
      {result.planetTypes.map((planet) => row.planetTypes.includes(planet) ? <td key={planet}><span aria-label="sí">●</span></td> : <td key={planet} className="pi-off"><span aria-label="no">·</span></td>)}
    </tr>)}</tbody>
    <tfoot><tr><td>Cubre</td><td />{result.planetTypes.map((planet, index) => <td key={planet} className={counts[index] === best ? 'pi-best' : undefined}>{counts[index]}/{result.extraction.length}</td>)}</tr></tfoot>
  </table></div>;
}

function ChainView() {
  const catalog = useApiQuery(endpoints.piSchematics);
  const [typeId, setTypeId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [result, setResult] = useState<PiChainResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const valid = Number.isInteger(Number(quantity)) && Number(quantity) >= 1 && Number(quantity) <= 10000000;
  const tiers = useMemo(() => {
    const groups = new Map<number, PiProduct[]>();
    (catalog.data ?? []).forEach((item) => groups.set(item.tier, [...(groups.get(item.tier) ?? []), item]));
    return [...groups.entries()];
  }, [catalog.data]);

  useEffect(() => {
    if (!typeId || !valid) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    const timer = window.setTimeout(() => {
      void endpoints.piChain(Number(typeId), Number(quantity))
        .then((value) => { if (!cancelled) setResult(value); })
        .catch((caught: Error) => { if (!cancelled) { setResult(null); setError(caught); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [typeId, quantity, valid, attempt]);

  return <>
    <Section>
      <div className="filters">
        <Field label="Producto" wide><select value={typeId} onChange={(event) => { setTypeId(event.target.value); setResult(null); }}>
          <option value="">Selecciona</option>
          {tiers.map(([tier, items]) => <optgroup key={tier} label={`P${tier}`}>{items.map((item) => <option key={item.typeId} value={item.typeId}>{item.name}</option>)}</optgroup>)}
        </select></Field>
        <Field label="Cantidad"><input type="number" min="1" max="10000000" aria-invalid={!valid} value={quantity} onChange={(event) => setQuantity(event.target.value)} /></Field>
      </div>
      {catalog.error && <ErrorState error={catalog.error} onRetry={() => void catalog.reload()} />}
      {catalog.data?.length === 0 && <EmptyState title="Sin esquemas PI" detail=" Importa los datos estáticos oficiales." />}
      {error && <ErrorState error={error} onRetry={() => setAttempt(attempt + 1)} />}
      {!typeId && !error && catalog.data && catalog.data.length > 0 && <EmptyState title="Selecciona un producto P1–P4" />}
      {loading && !result && !error && <LoadingState label="Calculando la cadena…" />}
    </Section>
    {result && <>
      {!result.stockCurrent && <Note tone="warn">Activos caducados o sin sincronizar ({dateTime(result.stockFetchedAt)}): el stock cubierto puede no ser real.</Note>}
      <Stats>
        <Stat label="P0 a extraer" value={`${number(result.extraction.length)} tipos`} />
        <Stat label="Sin colonia que lo produzca" value={result.withoutColony ? `■ ${number(result.withoutColony)}` : '0'} tone={result.withoutColony ? 'neg' : undefined} />
        <Stat label="Ciclos de fábrica" value={number(result.cycles)} />
      </Stats>
      <Section title="Cadena de producción">
        <div className="wf-tree pi-tree" role="region" aria-label="Árbol de producción" tabIndex={0}><ChainNode node={result.root} root /></div>
        <p className="fine">Borde grueso rojo y ■: ninguna colonia lo produce. Borde grueso azul y ◆: lo produce una de tus colonias. Borde fino verde y ✓: el stock lo cubre. Cubierto: stock en hangar y contenedores de estación más excedente de ciclos anteriores, asignado en orden del árbol.</p>
      </Section>
      {result.extraction.length > 0 && <Section title="Qué planeta sirve"><PlanetMatrix result={result} /></Section>}
    </>}
  </>;
}

export function PiPage() {
  const [view, setView] = useState<'colonies' | 'chain'>('colonies');
  return <Page title="PI" actions={<div className="segments" role="group" aria-label="Vistas de PI">
    <button type="button" aria-pressed={view === 'colonies'} onClick={() => setView('colonies')}>Colonias</button>
    <button type="button" aria-pressed={view === 'chain'} onClick={() => setView('chain')}>Cadena</button>
  </div>}>
    <div className="wf-result">{view === 'colonies' ? <ColoniesView /> : <ChainView />}</div>
  </Page>;
}
